/**
 * Правая колонка: карточка чата (§4 ТЗ).
 *
 * Внутренние заметки отображаются только если поле пришло с сервера. Проверка
 * `'internalNotes' in chat`, а не `chat.internalNotes !== null` — принципиальна:
 * сервер физически не присылает поле тем, у кого нет права, и пустая заметка
 * («поле есть, значение null») отличается от отсутствия доступа.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { TelegramChatDto } from "@tw/shared-types";
import { useSession } from "../state/session.tsx";
import { accountLabel, accountSubtitle } from "./accountLabel.js";
import { ArchiveIcon, BellIcon, BellOffIcon, PinIcon, PinOffIcon } from "./icons.tsx";
import type { ChatMembersDto, ChatProfileDto, FolderDto, StoryDto } from "../lib/api.js";
import { StoryViewer } from "./StoryViewer.tsx";
import { CHAT_TYPE_LABELS } from "./chatType.js";
import { chatTitle } from "./chatTitle.js";
import { ChatMedia } from "./ChatMedia.tsx";
import { Avatar } from "./Avatar.tsx";
import { cn } from "@/lib/utils";
import { StateBadge } from "./StateBadge.tsx";

interface ChatDetailsProps {
  /** Все папки компании. Нужны, чтобы показать в том числе пустые. */
  folders: FolderDto[];
  chat: TelegramChatDto;
  onUpdated: (chat: TelegramChatDto) => void;
  /** Открыть личный чат с участником группы по его Telegram-id. */
  onOpenMember?: (telegramUserId: string) => void;
}

/*
 * Стеклянная карточка раздела.
 *
 * Полупрозрачный фон с backdrop-blur, а не сплошная заливка: правая колонка
 * лежит поверх подложки переписки, и просвечивающая карточка привязывает её к
 * содержимому, вместо того чтобы висеть отдельной плитой.
 */
const SECTION =
  "flex flex-col gap-2.5 rounded-xl border border-border bg-card/70 p-4 shadow-sm backdrop-blur-md";

/** Заголовок раздела: капитель, а не полноразмерный заголовок. */
const CAPTION =
  "text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground";

/**
 * Пара «подпись — значение» в две колонки.
 *
 * Значение прижато вправо и обрезается: колонка узкая, а имена собеседников
 * длину не выбирают. `min-w-0` на <dd> обязателен — без него truncate в
 * гриде не срабатывает.
 */
const DL =
  "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1.5 text-sm " +
  "[&_dt]:text-xs [&_dt]:text-muted-foreground " +
  "[&_dd]:m-0 [&_dd]:min-w-0 [&_dd]:truncate [&_dd]:text-right [&_dd]:font-medium [&_dd]:text-heading";

/**
 * Статусы чата и их цвета.
 *
 * Цвет здесь несёт смысл, а не украшает: в списке из полусотни чатов состояние
 * работы — первое, что нужно понять, и читать его словами по одному значит
 * читать весь список. Оттенки взяты из статусных токенов системы, а не
 * подобраны: те же цвета уже означают «хорошо», «ждём», «проблема» в других
 * местах, и заводить для чатов вторую палитру значило бы учить их дважды.
 */
const STATUSES = [
  { value: "NEW", label: "Новый", tone: "var(--accent-fill)" },
  { value: "IN_PROGRESS", label: "В работе", tone: "var(--ok)" },
  { value: "WAITING", label: "Ожидание", tone: "var(--warn)" },
  { value: "DONE", label: "Завершён", tone: "var(--muted)" },
  { value: "ARCHIVED", label: "Архив", tone: "var(--muted)" },
];

export function ChatDetails({ chat, onUpdated, folders, onOpenMember }: ChatDetailsProps) {
  const { api, can } = useSession();

  const currentStatus = STATUSES.find((s) => s.value === chat.internalStatus);

  const canEditMetadata = can("telegram.chat.metadata.update");
  // Отсутствие поля = нет права. Сервер его не строит для таких зрителей.
  const notesVisible = Object.hasOwn(chat, "internalNotes");

  const [notes, setNotes] = useState(chat.internalNotes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<ChatMembersDto | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [profile, setProfile] = useState<ChatProfileDto | null>(null);
  const [stories, setStories] = useState<StoryDto[] | null>(null);
  const [storiesOpen, setStoriesOpen] = useState(false);

  /*
   * Профиль собеседника — телефон, username, «о себе», когда был в сети.
   *
   * Раньше карточка показывала только название и тип: менеджер, которому
   * нужно позвонить клиенту, шёл за номером в Telegram Desktop. Профиль
   * грузится при открытии чата и не роняет карточку, если Telegram его не
   * отдал: остальные поля читаются и без него.
   */
  useEffect(() => {
    setProfile(null);
    setStories(null);
    setStoriesOpen(false);
    let cancelled = false;
    api
      .chatProfile(chat.id)
      .then((data) => {
        if (cancelled) return;
        setProfile(data);
        if (data.hasStories) {
          void api
            .chatStories(chat.id)
            .then(({ items }) => {
              if (!cancelled) setStories(items);
            })
            .catch(() => undefined);
        }
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [api, chat.id]);

  useEffect(() => {
    setNotes(chat.internalNotes ?? "");
    setError(null);
  }, [chat.id, chat.internalNotes]);

  /*
   * Состав грузится только для групп и каналов и только при открытии чата.
   * Для личной переписки вопрос бессмысленный, а лишний запрос на каждый
   * переход между чатами — лишняя нагрузка на TDLib.
   */
  useEffect(() => {
    setMembers(null);
    setMembersOpen(false);

    if (chat.type === "private" || chat.type === "secret") return;

    let cancelled = false;
    api
      .chatMembers(chat.id)
      .then((data) => {
        if (!cancelled) setMembers(data);
      })
      .catch(() => {
        // Недоступный состав не должен ломать карточку: остальные поля
        // читаются и без него.
        if (!cancelled) setMembers(null);
      });

    return () => {
      cancelled = true;
    };
  }, [api, chat.id, chat.type]);

  const patch = async (
    body: Parameters<typeof api.updateChat>[1],
  ): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      onUpdated(await api.updateChat(chat.id, body));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  /**
   * Положить чат в папку или вынуть.
   *
   * После ответа перечитываем чат целиком, а не правим folderIds на месте:
   * сервер мог отвергнуть часть операции, и локальная догадка о результате
   * разошлась бы с действительностью.
   */
  const toggleFolder = async (
    folderId: string,
    attached: boolean,
  ): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await api.setChatFolder(folderId, chat.id, attached);
      onUpdated(await api.chat(chat.id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Не удалось изменить папку",
      );
    } finally {
      setSaving(false);
    }
  };

  const accountSub = accountSubtitle(chat.account);

  return (
    /*
     * Каждый раздел — стеклянная карточка, а не абзац на общем фоне.
     *
     * Раньше здесь были голые <dl> без единого класса: браузер рисует <dd> с
     * отступом и с новой строки, и подпись со значением сливались в плоский
     * столбик — «Собеседник / Stella / Тип / Личный» читалось как список из
     * четырёх равнозначных строк. Сетка из двух колонок разводит роли: слева
     * приглушённая подпись, справа значение.
     */
    <div className="flex flex-col gap-3 p-4">
      {/*
        Шапка карточки: аватар, имя, подпись — как в профиле Telegram.

        Раньше панель открывалась двумя коробками подряд, где первой строкой
        шла подпись «Собеседник», а рядом — имя. Имя чата, ради которого
        панель и открывают, стояло значением поля наравне с типом и username.
        Здесь оно заголовок, и панель наконец отвечает на вопрос «чей это
        чат» до того, как её начали читать.
      */}
      <div className="flex flex-col items-center gap-2 px-2 pb-1 pt-2 text-center">
        {/*
          Кольцо вокруг аватара — есть истории; нажатие открывает их. Тот же
          язык, что в Telegram: человек его уже знает, объяснять не нужно.
          Серое кольцо — все просмотрены.
        */}
        {stories && stories.length > 0 ? (
          <button
            type="button"
            onClick={() => setStoriesOpen(true)}
            title="Показать истории"
            className={cn(
              "rounded-full p-[3px] ring-2 ring-offset-2 ring-offset-card transition-transform active:scale-95",
              stories.some((s) => !s.viewed) ? "ring-brand" : "ring-border",
            )}
          >
            <Avatar
              name={chatTitle(chat.title)}
              seed={chat.id}
              size="lg"
              photoUrl={chat.hasPhoto ? `/api/chats/${chat.id}/photo` : null}
            />
          </button>
        ) : (
          <Avatar
            name={chatTitle(chat.title)}
            seed={chat.id}
            size="lg"
            photoUrl={chat.hasPhoto ? `/api/chats/${chat.id}/photo` : null}
          />
        )}
        {storiesOpen && stories && (
          <StoryViewer
            chatId={chat.id}
            title={chatTitle(chat.title)}
            stories={stories}
            onClose={() => {
              setStoriesOpen(false);
              // После просмотра кольцо гаснет — Telegram уже засчитал.
              setStories((prev) => prev?.map((s) => ({ ...s, viewed: true })) ?? prev);
            }}
          />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-[17px] font-semibold tracking-[-0.019em] text-heading">
            {chatTitle(chat.title)}
          </p>
          <p className="text-[12px] text-muted-foreground">
            {[
              CHAT_TYPE_LABELS[chat.type] ?? chat.type,
              chat.peerUsername ? `@${chat.peerUsername}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>

      {profile && (
        <section className={SECTION}>
          <h3 className={CAPTION}>
            {profile.kind === "channel"
              ? "О канале"
              : profile.kind === "group"
                ? "О группе"
                : "О собеседнике"}
          </h3>
          <dl className={DL}>
            {profile.username && (
              <>
                <dt>Username</dt>
                <dd className="select-text">@{profile.username}</dd>
              </>
            )}
            {profile.phone && (
              <>
                <dt>Телефон</dt>
                <dd className="select-text">+{profile.phone}</dd>
              </>
            )}
            {profile.lastSeen && profile.lastSeen.kind !== "hidden" && (
              <>
                <dt>В сети</dt>
                <dd>{formatLastSeen(profile.lastSeen)}</dd>
              </>
            )}
            {typeof profile.memberCount === "number" && (
              <>
                <dt>{profile.kind === "channel" ? "Подписчики" : "Участники"}</dt>
                <dd>{profile.memberCount.toLocaleString("ru-RU")}</dd>
              </>
            )}
            {(profile.isPremium || profile.isVerified || profile.isScam) && (
              <>
                <dt>Отметки</dt>
                <dd>
                  {[
                    profile.isVerified ? "подтверждён" : null,
                    profile.isPremium ? "Premium" : null,
                    profile.isScam ? "помечен как мошенник" : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </dd>
              </>
            )}
          </dl>
          {(profile.bio || profile.description) && (
            <p className="mt-1 select-text whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/90">
              {profile.bio ?? profile.description}
            </p>
          )}
          {!profile.username &&
            !profile.phone &&
            !profile.bio &&
            !profile.description &&
            (!profile.lastSeen || profile.lastSeen.kind === "hidden") && (
              <p className="text-xs text-muted-foreground">
                Telegram не раскрывает подробности этого профиля
              </p>
            )}
        </section>
      )}

      <section className={SECTION}>
        <h3 className={CAPTION}>Telegram-аккаунт</h3>
        <dl className={DL}>
          <dt>Аккаунт</dt>
          <dd>{accountLabel(chat.account)}</dd>
          {/* Для стажёра здесь ничего нет — и это правильно, а не «данные не
              загрузились». */}
          {accountSub && (
            <>
              <dt>Контакт</dt>
              <dd>{accountSub}</dd>
            </>
          )}
          <dt>Состояние</dt>
          <dd>
            {/*
              Состояние — цветная метка, а не сырое значение перечисления.
              «CONNECTED» капслоком латиницей посреди русского интерфейса
              читалось как отладочный вывод, а не как «аккаунт на связи», и
              главное — не отличалось видом от неисправного состояния.
            */}
            <StateBadge state={chat.account.connectionState} />
          </dd>
        </dl>
        {/*
          Ответственные — в той же карточке, за тонкой чертой.

          Раньше это была отдельная коробка, и панель открывалась стопкой из
          шести обведённых блоков подряд. Рамка вокруг двух строк текста ничего
          не разделяет — она только добавляет линий, и колонка читалась как
          набор карточек, а не как одна карточка чата. Черта разделяет ровно
          так же, но не обводит.
        */}
        <div className="mt-1 border-t border-border/60 pt-3">
          <h3 className={cn(CAPTION, "mb-2")}>Ответственные</h3>
          <dl className={DL}>
            <dt>Менеджер</dt>
            <dd>{chat.assignedManager?.displayName ?? "не назначен"}</dd>
            <dt>Старший менеджер</dt>
            <dd>{chat.assignedSenior?.displayName ?? "—"}</dd>
          </dl>
        </div>
      </section>

      <section className={SECTION}>
        <h3 className={CAPTION}>Статус</h3>

        {/*
          Точка цвета стоит РЯДОМ со списком, а не внутри него: у нативного
          <option> нет надёжного способа покрасить содержимое — Windows и
          Linux рисуют список средствами системы и стили игнорируют. Точка
          снаружи работает везде одинаково.
        */}
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="size-2.5 flex-none rounded-full"
            style={{ background: currentStatus?.tone ?? "var(--muted)" }}
          />

          {canEditMetadata ? (
            <select
              className="min-w-0 flex-1"
              value={chat.internalStatus}
              disabled={saving}
              onChange={(e) => void patch({ internalStatus: e.target.value })}
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-sm">{currentStatus?.label}</span>
          )}
        </div>

        {/*
          Архивация вынесена отдельной кнопкой, а не оставлена одним из
          пунктов списка статусов. Это самое частое завершающее действие, и
          прятать его в выпадающий список значит требовать три клика вместо
          одного. Возврат из архива тем же элементом — чтобы не пришлось
          искать, каким статусом чат был до архивации.
        */}
        {/*
          Беззвучность — НАША и поимённая, в Telegram она не уходит.
          Аккаунт общий на нескольких менеджеров: телеграмная настройка
          заглушила бы чат всем разом, включая тех, кто об этом не просил.
          Поэтому подпись говорит прямо, что это личная настройка.
        */}
        {/*
          Действия над чатом — единый набор строк: иконка в цветном чипе,
          подпись слева. Раньше это были три разнородные бледные таблетки,
          которые сливались с панелью. Активное состояние (без звука,
          закреплён) подсвечивается — видно с одного взгляда.
        */}
        <div className="mt-3 flex flex-col gap-1.5">
          <ActionRow
            icon={chat.muted ? <BellIcon /> : <BellOffIcon />}
            label={chat.muted ? "Включить оповещения" : "Без звука для меня"}
            tone={chat.muted ? "brand" : "muted"}
            disabled={saving}
            onClick={() => {
              setSaving(true);
              setError(null);
              const action = chat.muted
                ? api.unmuteChat(chat.id)
                : api.muteChat(chat.id);
              void action
                .then(() => api.chat(chat.id))
                .then(onUpdated)
                .catch((err: unknown) =>
                  setError(err instanceof Error ? err.message : "Не удалось изменить"),
                )
                .finally(() => setSaving(false));
            }}
          />

          {canEditMetadata && (
            <ActionRow
              icon={chat.isPinned ? <PinOffIcon /> : <PinIcon />}
              label={chat.isPinned ? "Открепить в Telegram" : "Закрепить в Telegram"}
              tone={chat.isPinned ? "brand" : "muted"}
              disabled={saving}
              onClick={() => {
                setSaving(true);
                setError(null);
                void api
                  .pinChat(chat.id, !chat.isPinned)
                  .then(onUpdated)
                  .catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : "Не удалось изменить"),
                  )
                  .finally(() => setSaving(false));
              }}
            />
          )}

          {canEditMetadata && (
            <ActionRow
              icon={<ArchiveIcon />}
              label={
                chat.internalStatus === "ARCHIVED" ? "Вернуть из архива" : "В архив"
              }
              tone="warn"
              disabled={saving}
              onClick={() =>
                void patch({
                  internalStatus:
                    chat.internalStatus === "ARCHIVED" ? "DONE" : "ARCHIVED",
                })
              }
            />
          )}
        </div>
      </section>

      {/*
        Папки перечисляются все, включая пустые: иначе положить чат в новую
        папку было бы некуда, пока в ней нет ни одного чата.
      */}
      {/*
        Число участников читается ЗАЩИЩЁННО.
        Прямое обращение к memberCount роняло не только эту секцию, а весь
        рабочий экран: границы ошибок над карточкой нет, и React снимает всё
        поддерево. Достаточно ответа старой версии API — или любого, где поля
        не оказалось, — чтобы менеджер увидел белый экран вместо переписки.

        Правило в проекте общее (см. entities и folderIds), и это место было
        из него выпавшим.
      */}
      {members && typeof members.memberCount === "number" && (
        <section className={SECTION}>
          <h3 className={CAPTION}>Участники</h3>

          <p className="text-sm font-medium text-heading">
            {members.memberCount.toLocaleString("ru-RU")}{" "}
            {members.kind === "channel" ? "подписчиков" : "участников"}
          </p>

          {members.canViewMembers && members.members.length > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setMembersOpen((v) => !v)}
                className="self-start text-xs text-brand transition-colors hover:underline"
              >
                {membersOpen ? "Скрыть список" : "Показать список"}
              </button>

              {membersOpen && (
                <ul className="flex flex-col gap-0.5">
                  {members.members.map((m) => {
                    const name =
                      m.displayName?.trim() ||
                      (m.username ? `@${m.username}` : null) ||
                      (m.telegramUserId ? `ID ${m.telegramUserId}` : "—");
                    const role =
                      m.status === "Creator"
                        ? "владелец"
                        : m.status === "Administrator"
                          ? "админ"
                          : null;
                    // Открыть можно того, у кого есть id: жмём — заводится
                    // личный чат с участником от лица этого же аккаунта.
                    const openable = Boolean(onOpenMember && m.telegramUserId);
                    return (
                      <li key={m.telegramUserId ?? Math.random()}>
                        <button
                          type="button"
                          disabled={!openable}
                          onClick={() =>
                            openable && onOpenMember?.(m.telegramUserId as string)
                          }
                          className={cn(
                            "flex w-full items-center gap-2 rounded-lg border-transparent bg-transparent px-2 py-1.5 text-left text-[13px] transition-colors",
                            openable ? "hover:bg-accent" : "cursor-default",
                          )}
                          title={openable ? "Открыть переписку" : undefined}
                        >
                          <Avatar name={name} seed={m.telegramUserId ?? name} size="xs" />
                          <span className="min-w-0 flex-1 truncate font-medium text-heading">
                            {name}
                          </span>
                          {role && (
                            <span className="flex-none rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                              {role}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                  {/*
                    Telegram раскрывает состав крупной группы только
                    администраторам. Для обычного участника список обрывается
                    на нескольких записях — это ограничение платформы, и о нём
                    честнее сказать, чем показать неполный список как полный.
                  */}
                  {members.members.length < members.memberCount && (
                    <li className="pt-1 opacity-70">
                      Telegram раскрывает лишь часть состава: полный список
                      виден администраторам группы.
                    </li>
                  )}
                </ul>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Telegram не раскрывает состав этого чата
            </p>
          )}
        </section>
      )}

      {canEditMetadata && folders.length > 0 && (
        <section className={SECTION}>
          <h3 className={CAPTION}>Папки</h3>
          <div className="flex flex-col gap-1">
            {folders.map((folder) => {
              // Тот же случай, что и с entities: у прошлой версии API поля нет.
              const inFolder = (chat.folderIds ?? []).includes(folder.id);

              return (
                <label
                  key={folder.id}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-sm transition-colors hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    className="accent-[var(--accent-fill)]"
                    checked={inFolder}
                    disabled={saving}
                    onChange={(e) =>
                      void toggleFolder(folder.id, e.target.checked)
                    }
                  />
                  {folder.name}
                </label>
              );
            })}
          </div>
        </section>
      )}

      {/*
        Вложения стоят выше тегов и заметок намеренно: к ним возвращаются
        постоянно, а теги и заметки читают изредка. Порядок разделов — это и
        есть частота обращения, а не логическая стройность.
      */}
      <section className={SECTION}>
        <h3 className={CAPTION}>Вложения</h3>
        <ChatMedia chatId={chat.id} />
      </section>

      {chat.tags.length > 0 && (
        <section className={SECTION}>
          <h3 className={CAPTION}>Теги</h3>
          <div className="flex flex-wrap gap-1">
            {chat.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-secondary px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {notesVisible && (
        <section className={SECTION}>
          <h3 className={CAPTION}>Внутренние заметки</h3>
          <p className="text-xs text-muted-foreground">
            Видны только сотрудникам. В Telegram не отправляются.
          </p>

          {canEditMetadata ? (
            <>
              <textarea
                className="min-h-20 w-full resize-y rounded-lg border border-border bg-background p-2.5 text-sm outline-none transition-colors focus:border-ring"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={6}
                disabled={saving}
              />
              <button
                type="button"
                className="rounded-full border-transparent bg-primary py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:opacity-45"
                disabled={saving || notes === (chat.internalNotes ?? "")}
                onClick={() => void patch({ internalNotes: notes })}
              >
                {saving ? "Сохранение…" : "Сохранить"}
              </button>
            </>
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
              {chat.internalNotes || "—"}
            </p>
          )}
        </section>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * «В сети» словами.
 *
 * Точное время Telegram отдаёт не всем: приблизительные статусы приходят,
 * когда человек скрыл его в настройках приватности, и превращать их в дату
 * нельзя — это ровно та информация, которую он спрятал.
 */
function formatLastSeen(seen: NonNullable<ChatProfileDto["lastSeen"]>): string {
  switch (seen.kind) {
    case "online":
      return "сейчас";
    case "offline": {
      if (!seen.at) return "недавно";
      const at = new Date(seen.at);
      const sameDay = at.toDateString() === new Date().toDateString();
      return sameDay
        ? `сегодня в ${at.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}`
        : at.toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    }
    case "recently":
      return "недавно";
    case "lastWeek":
      return "на этой неделе";
    case "lastMonth":
      return "в этом месяце";
    default:
      return "скрыто";
  }
}

/**
 * Строка действия над чатом — иконка в цветном чипе + подпись.
 *
 * Один вид на все действия панели «Статус»: беззвучность, закреп, архив.
 * `tone` красит чип и подпись у активного/особого действия, чтобы состояние
 * читалось без чтения текста.
 */
function ActionRow({
  icon,
  label,
  tone,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  tone: "muted" | "brand" | "warn";
  disabled?: boolean;
  onClick: () => void;
}) {
  const chip =
    tone === "brand"
      ? "bg-brand/12 text-brand"
      : tone === "warn"
        ? "bg-[var(--warn)]/12 text-[var(--warn)]"
        : "bg-secondary text-muted-foreground";
  const text = tone === "brand" ? "text-brand" : "text-soft";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-xl border border-transparent bg-secondary/50 px-2.5 py-2 text-left transition-colors hover:bg-accent disabled:opacity-50",
      )}
    >
      <span
        className={cn(
          "grid size-8 flex-none place-items-center rounded-lg transition-colors [&_svg]:size-[17px]",
          chip,
        )}
      >
        {icon}
      </span>
      <span className={cn("text-[13.5px] font-medium", text)}>{label}</span>
    </button>
  );
}
